/* ============================================================================
   HACHIMAN CUSTOM BUILDER v3 — engine
   Component-first builder over the store's OWN lineup products.

   Data: inline #hbuilder-data JSON, server-rendered by the section from the
   Hachiman console (machines = lineup products with their variant options;
   warrantyOptions = the warranty product's tiers). No fetches, no snapshots.

   Model: the component categories are the UNION of the machines' option
   values. Every full set of picks resolves to a REAL variant. Picking an
   option no machine pairs with your current build auto-resolves the rest of
   the build to the cheapest real machine that includes your choice (nothing
   is disabled unless it is sold out everywhere). Deltas are relative to the
   current build.
   Checkout posts the resolved variant (+ warranty companion line) to the
   NATIVE Hachiman cart via /cart/add.js.
   ========================================================================== */
(function () {
  'use strict';

  var rootEl = document.getElementById('hbuilder-root');
  var dataEl = document.getElementById('hbuilder-data');
  if (!rootEl || !dataEl) return;

  var DATA;
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }
  var MACHINES = (DATA.machines || []).filter(function (m) { return m && m.variants && m.variants.length; });
  var WARRANTY = DATA.warrantyOptions || [];
  if (!MACHINES.length) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── utilities ────────────────────────────────────────────────────────── */
  function money(cents) { return '$' + (Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function fmtDelta(cents) {
    if (cents === 0) return '$0';
    var n = Math.round(Math.abs(cents) / 100).toLocaleString('en-US');
    return (cents < 0 ? '- $' : '+ $') + n;
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function normKey(name) { return String(name).split('|')[0].trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function displayName(name) { return String(name).split('|')[0].replace(/\s+/g, ' ').trim(); }
  function splitValue(v) {
    var parts = String(v).split(/\s*∣\s*|\s*\|\s*|\s+I\s+/);
    return { primary: (parts[0] || '').trim(), secondary: parts.slice(1).join(' · ').trim() };
  }

  /* ── build the union category model ───────────────────────────────────── */
  // categories: [{ key, label, kind, values: [string] }]
  var CATS = [];
  var catByKey = {};
  MACHINES.forEach(function (m) {
    m._idx = {}; // catKey -> option position for this machine
    m.options.forEach(function (opt, oi) {
      var key = normKey(opt.name);
      m._idx[key] = oi;
      var c = catByKey[key];
      if (!c) {
        c = { key: key, label: displayName(opt.name), values: [] };
        catByKey[key] = c;
        CATS.push(c);
      }
      opt.values.forEach(function (v) {
        if (c.values.indexOf(v) === -1) c.values.push(v);
      });
    });
  });

  function kindOf(label, i) {
    var l = label.toLowerCase();
    if (l.indexOf('cpu') !== -1) return 'cpu';
    if (l.indexOf('gpu') !== -1) return 'gpu';
    if (l.indexOf('storage') !== -1 || l.indexOf('memory') !== -1) return 'storage';
    return i === 0 ? 'cpu' : 'gpu';
  }
  CATS.forEach(function (c, i) { c.kind = kindOf(c.label, i); });

  /* variants matching a picks map (catKey -> value) */
  function candidates(picks) {
    var found = [];
    MACHINES.forEach(function (m) {
      m.variants.forEach(function (v) {
        for (var i = 0; i < CATS.length; i++) {
          var key = CATS[i].key;
          var oi = m._idx[key];
          if (oi === undefined) return; // machine lacks the group entirely
          if (picks[key] !== undefined && v.options[oi] !== picks[key]) return;
        }
        found.push({ machine: m, variant: v });
      });
    });
    return found;
  }
  function cheapest(list) {
    var best = null;
    list.forEach(function (c) {
      if (!c.variant.available) return;
      if (!best || c.variant.price < best.variant.price) best = c;
    });
    return best;
  }

  // Resolve a pick: cheapest REAL build that includes `value` in `catKey`,
  // preferring the machine that keeps the most of the current picks. Because
  // categories are a union across machines, some picks force companion
  // changes — those come back in .changed.
  function resolvePick(catKey, value) {
    var best = null, bestScore = -1;
    MACHINES.forEach(function (m) {
      var oi = m._idx[catKey];
      if (oi === undefined) return;
      m.variants.forEach(function (v) {
        if (!v.available || v.options[oi] !== value) return;
        var score = 0;
        for (var i = 0; i < CATS.length; i++) {
          var ck = CATS[i].key;
          if (ck === catKey) continue;
          var ci = m._idx[ck];
          if (ci !== undefined && state.picks[ck] === v.options[ci]) score++;
        }
        if (score > bestScore || (score === bestScore && (!best || v.price < best.variant.price))) {
          best = { machine: m, variant: v }; bestScore = score;
        }
      });
    });
    if (!best) return null;
    var picks = {}, changed = [];
    CATS.forEach(function (c) {
      var ci = best.machine._idx[c.key];
      var nv = (ci !== undefined) ? best.variant.options[ci] : state.picks[c.key];
      picks[c.key] = nv;
      if (c.key !== catKey && state.picks[c.key] !== nv) changed.push(c);
    });
    return { picks: picks, resolution: best, changed: changed };
  }

  /* global default: the cheapest available variant across the lineup */
  function defaultPicks() {
    var best = cheapest(candidates({}));
    if (!best) return null;
    var picks = {};
    CATS.forEach(function (c) {
      var oi = best.machine._idx[c.key];
      if (oi !== undefined) picks[c.key] = best.variant.options[oi];
    });
    return picks;
  }

  /* sort each category's values by the cheapest build that contains them */
  CATS.forEach(function (c) {
    c.values.sort(function (a, b) {
      var pa = {}, pb = {};
      pa[c.key] = a; pb[c.key] = b;
      var ba = cheapest(candidates(pa)), bb = cheapest(candidates(pb));
      return (ba ? ba.variant.price : 1 / 0) - (bb ? bb.variant.price : 1 / 0);
    });
  });

  /* ── state ────────────────────────────────────────────────────────────── */
  var state = {
    picks: defaultPicks() || {},
    warranty: WARRANTY.length ? WARRANTY[0].label : null,
    sheetOpen: false,
    adding: false,
    added: false,
    error: null,
    flash: '',
  };
  var revealedOnce = false;
  var booted = false;
  var bootNotice = '';

  function resolution() { return cheapest(candidates(state.picks)); }
  function warrantyPrice(label) {
    for (var i = 0; i < WARRANTY.length; i++) if (WARRANTY[i].label === label) return WARRANTY[i].price;
    return 0;
  }
  function total() {
    var r = resolution();
    return (r ? r.variant.price : 0) + warrantyPrice(state.warranty);
  }

  /* ── build-share hash: #b=0.2,1.4,2.0,w.1 (cat index . value index) ───── */
  function writeHash() {
    var parts = [];
    CATS.forEach(function (c, ci) {
      var vi = c.values.indexOf(state.picks[c.key]);
      if (vi !== -1) parts.push(ci + '.' + vi);
    });
    if (state.warranty) {
      for (var wi = 0; wi < WARRANTY.length; wi++) {
        if (WARRANTY[wi].label === state.warranty) { parts.push('w.' + wi); break; }
      }
    }
    try { history.replaceState(null, '', '#b=' + parts.join(',')); } catch (e) { /* noop */ }
  }
  function readHash() {
    var m = (location.hash || '').match(/#b=([\w.,]+)/);
    if (!m) return { applied: 0, failed: 0 };
    var applied = 0, failed = 0;
    var next = {};
    CATS.forEach(function (c) { next[c.key] = state.picks[c.key]; });
    var wLabel = state.warranty;
    m[1].split(',').forEach(function (pair) {
      var kv = pair.split('.');
      if (kv[0] === 'w') {
        var w = WARRANTY[parseInt(kv[1], 10)];
        if (w) { wLabel = w.label; applied++; } else { failed++; }
        return;
      }
      var c = CATS[parseInt(kv[0], 10)];
      var v = c && c.values[parseInt(kv[1], 10)];
      if (v !== undefined) { next[c.key] = v; applied++; } else { failed++; }
    });
    // only accept a combination some machine actually sells
    if (applied && cheapest(candidates(next))) {
      state.picks = next;
      state.warranty = wLabel;
    } else if (applied) {
      failed += applied;
      applied = 0;
    }
    return { applied: applied, failed: failed };
  }

  /* ── component art (original inline SVG, brand-tinted) ────────────────── */
  function brandOf(label) {
    var l = label.toLowerCase();
    if (l.indexOf('nvidia') !== -1 || l.indexOf('rtx') !== -1) return 'nvidia';
    if (l.indexOf('amd') !== -1 || l.indexOf('radeon') !== -1 || l.indexOf('ryzen') !== -1) return 'amd';
    return '';
  }
  function tintOf(brand) {
    if (brand === 'nvidia') return '#9fe870';
    if (brand === 'amd') return '#ff7b7b';
    return '#c62026';
  }
  function art(kind, tint) {
    var t = tint || '#c62026';
    if (kind === 'cpu') {
      return '<svg viewBox="0 0 34 34" fill="none" aria-hidden="true"><rect x="7" y="7" width="20" height="20" rx="3" stroke="' + t + '" stroke-width="1.6"/><rect x="12.5" y="12.5" width="9" height="9" rx="1.5" fill="' + t + '" opacity="0.85"/><path stroke="' + t + '" stroke-width="1.6" stroke-linecap="round" d="M12 7V3M17 7V3M22 7V3M12 31v-4M17 31v-4M22 31v-4M7 12H3M7 17H3M7 22H3M31 12h-4M31 17h-4M31 22h-4"/></svg>';
    }
    if (kind === 'gpu') {
      return '<svg viewBox="0 0 34 34" fill="none" aria-hidden="true"><rect x="3" y="10" width="28" height="13" rx="2.5" stroke="' + t + '" stroke-width="1.6"/><circle cx="13" cy="16.5" r="4" stroke="' + t + '" stroke-width="1.6"/><circle cx="24" cy="16.5" r="2.6" stroke="' + t + '" stroke-width="1.6"/><path stroke="' + t + '" stroke-width="1.6" stroke-linecap="round" d="M6 23v4h6M31 14v6"/></svg>';
    }
    if (kind === 'storage') {
      return '<svg viewBox="0 0 34 34" fill="none" aria-hidden="true"><rect x="4" y="12" width="26" height="10" rx="2" stroke="' + t + '" stroke-width="1.6"/><rect x="8" y="15.2" width="7" height="3.6" rx="1" fill="' + t + '" opacity="0.85"/><path stroke="' + t + '" stroke-width="1.6" stroke-linecap="round" d="M20 15.2v3.6M23.5 15.2v3.6M27 15.2v3.6"/></svg>';
    }
    return '<svg viewBox="0 0 34 34" fill="none" aria-hidden="true"><path d="M17 3l11 4v9c0 7.2-4.7 12.6-11 15C10.7 28.6 6 23.2 6 16V7l11-4z" stroke="' + t + '" stroke-width="1.6" stroke-linejoin="round"/><path d="M12 16.5l3.4 3.4L23 12.4" stroke="' + t + '" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  /* ── rendering ────────────────────────────────────────────────────────── */
  function cardHTML(c, value, curPrice) {
    var pickd = state.picks[c.key] === value;
    var sv = splitValue(value);
    var res = pickd ? null : resolvePick(c.key, value);
    var soldOut = !pickd && !res;
    var pill;
    if (pickd) pill = '<span class="hb-delta hb-delta--in">IN BUILD</span>';
    else if (soldOut) pill = '<span class="hb-delta">Sold out</span>';
    else {
      var d = res.resolution.variant.price - curPrice;
      pill = '<span class="hb-delta' + (d < 0 ? ' hb-delta--save' : '') + '">' + esc(fmtDelta(d)) + '</span>';
    }
    var brand = brandOf(value);
    var brandChip = brand ? '<span class="hb-brand hb-brand--' + brand + '">' + brand.toUpperCase() + '</span>' : '';
    var note = soldOut ? '<span class="hb-brand">Sold out everywhere</span>' : '';
    return (
      '<button type="button" class="hb-card hb-card--row" data-cat="' + esc(c.key) + '" data-value="' + esc(value) + '"' +
        ' aria-pressed="' + pickd + '"' + (soldOut ? ' disabled' : '') + '>' +
        '<span class="hb-option-art">' + art(c.kind, tintOf(brand)) + '</span>' +
        '<span class="hb-card-body"><p class="hb-card-name">' + esc(sv.primary) + '</p>' +
        (sv.secondary ? '<p class="hb-card-sub2">' + esc(sv.secondary) + '</p>' : '') +
        '<span class="hb-card-foot">' + brandChip + note + '</span></span>' +
        pill +
      '</button>'
    );
  }

  function warrantyHTML(idx) {
    if (!WARRANTY.length) return '';
    var curW = warrantyPrice(state.warranty);
    var cards = WARRANTY.map(function (w) {
      var pickd = state.warranty === w.label;
      var d = w.price - curW;
      var pill = pickd
        ? '<span class="hb-delta hb-delta--in">IN BUILD</span>'
        : '<span class="hb-delta' + (d < 0 ? ' hb-delta--save' : '') + '">' + esc(fmtDelta(d)) + '</span>';
      return (
        '<button type="button" class="hb-card hb-card--row" data-warranty="' + esc(w.label) + '" aria-pressed="' + pickd + '">' +
          '<span class="hb-option-art">' + art('warranty') + '</span>' +
          '<span class="hb-card-body"><p class="hb-card-name">' + esc(w.label) + '</p></span>' + pill +
        '</button>'
      );
    }).join('');
    return sectionHTML('warranty', idx, 'Warranty', 'Lifetime tech support included with every machine.', cards);
  }

  function sectionHTML(id, num, title, sub, cards) {
    return (
      '<section class="hb-cat hb-reveal' + (revealedOnce ? ' hb-in' : '') + '" id="hb-cat-' + esc(id) + '" aria-label="' + esc(title) + '">' +
        '<div class="hb-cat-head"><span class="hb-cat-kicker">// 0' + (num + 1) + '</span>' +
        '<h2 class="hb-cat-title" tabindex="-1">' + esc(title) + '</h2></div>' +
        '<p class="hb-cat-sub">' + esc(sub) + '</p>' +
        '<div class="hb-grid hb-grid--rows">' + cards + '</div>' +
      '</section>'
    );
  }

  function railHTML(sheet) {
    var r = resolution();
    var rows = CATS.map(function (c) {
      return (
        '<button type="button" class="hb-rail-row" data-goto="hb-cat-' + esc(c.key) + '">' +
          '<span class="hb-rail-ico">' + art(c.kind) + '</span>' +
          '<span class="hb-rail-info"><span class="hb-rail-cat">' + esc(c.label) + '</span>' +
          '<span class="hb-rail-pick">' + esc(splitValue(state.picks[c.key] || '—').primary) + '</span></span>' +
        '</button>'
      );
    }).join('');
    if (WARRANTY.length) {
      rows += (
        '<button type="button" class="hb-rail-row" data-goto="hb-cat-warranty">' +
          '<span class="hb-rail-ico">' + art('warranty') + '</span>' +
          '<span class="hb-rail-info"><span class="hb-rail-cat">Warranty</span>' +
          '<span class="hb-rail-pick">' + esc(state.warranty || '—') + '</span></span>' +
        '</button>'
      );
    }
    var notice = bootNotice ? '<div class="hb-warn" role="status">⚠ ' + esc(bootNotice) + '</div>' : '';
    var flash = state.flash ? '<div class="hb-warn hb-warn--info" role="status">↺ ' + esc(state.flash) + '</div>' : '';
    return (
      '<aside class="hb-rail' + (sheet ? ' hb-rail--sheet' + (state.sheetOpen ? ' hb-open' : '') : '') + '" ' + (sheet ? 'id="hb-sheet"' : '') + ' aria-label="Your build">' +
        '<div class="hb-rail-head"><h2 class="hb-rail-title">// Your Loadout</h2>' +
        '<span class="hb-rail-sub">Configured your way — assembled by us</span></div>' +
        '<div class="hb-rail-rows">' + rows + '</div>' +
        notice + flash +
        (state.error ? '<div class="hb-warn" role="alert">' + esc(state.error) + '</div>' : '') +
        '<div class="hb-rail-foot">' +
          '<span class="hb-total-label">// Build Total</span>' +
          '<div class="hb-total" data-total aria-live="polite">' + esc(money(total())) + '</div>' +
          '<button type="button" class="hb-cta' + (state.added ? ' hb-cta--done' : '') + '" data-add' + (state.adding || !r ? ' disabled' : '') + '>' +
            (state.adding ? 'FORGING…' : state.added ? 'VIEW CART →' : 'ADD TO CART') +
          '</button>' +
          '<button type="button" class="hb-cta hb-cta--ghost" data-share>Copy Build Link</button>' +
          '<p class="hb-fulfil">Hand-assembled and stress-tested in California. Free continental-US shipping.</p>' +
        '</div>' +
      '</aside>'
    );
  }

  var observer = null;
  function observeReveals() {
    if (REDUCED || !('IntersectionObserver' in window)) {
      rootEl.querySelectorAll('.hb-reveal').forEach(function (el) { el.classList.add('hb-in'); });
      return;
    }
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('hb-in'); observer.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    rootEl.querySelectorAll('.hb-reveal').forEach(function (el) { observer.observe(el); });
  }

  var lastTotal = null;
  function animateTotals() {
    var els = rootEl.querySelectorAll('[data-total]');
    var target = total();
    if (REDUCED || lastTotal === null || lastTotal === target) {
      els.forEach(function (el) { el.textContent = money(target); });
      lastTotal = target;
      return;
    }
    var from = lastTotal;
    lastTotal = target;
    var t0 = performance.now(), DUR = 450;
    (function tick(now) {
      var k = Math.min((now - t0) / DUR, 1);
      var eased = 1 - Math.pow(1 - k, 3);
      var v = Math.round(from + (target - from) * eased);
      els.forEach(function (el) { el.textContent = money(v); });
      if (k < 1) requestAnimationFrame(tick);
    })(t0);
  }

  function render(focusSel) {
    var r = resolution();
    var curPrice = r ? r.variant.price : 0;
    var html = '<div class="hb-layout"><div class="hb-main">';
    CATS.forEach(function (c, i) {
      var cards = c.values.map(function (v) { return cardHTML(c, v, curPrice); }).join('');
      html += sectionHTML(c.key, i, c.label, 'Deltas are relative to your current build.', cards);
    });
    html += warrantyHTML(CATS.length);
    html += '</div>' + railHTML(false) + '</div>';
    html += railHTML(true);
    html += (
      '<div class="hb-dock">' +
        '<div class="hb-dock-total"><span class="hb-total-label">// Build Total</span>' +
        '<div class="hb-total" data-total>' + esc(money(total())) + '</div></div>' +
        '<button type="button" class="hb-cta hb-cta--ghost" data-sheet-toggle aria-expanded="' + state.sheetOpen + '" aria-controls="hb-sheet">' + (state.sheetOpen ? 'Close' : 'View Build') + '</button>' +
        '<button type="button" class="hb-cta' + (state.added ? ' hb-cta--done' : '') + '" data-add' + (state.adding ? ' disabled' : '') + '>' + (state.adding ? '…' : state.added ? 'CART →' : 'ADD TO CART') + '</button>' +
      '</div>'
    );
    rootEl.innerHTML = html;
    observeReveals();
    animateTotals();
    if (booted) writeHash();
    revealedOnce = true;
    if (focusSel) {
      var el = rootEl.querySelector(focusSel);
      if (el && el.focus) el.focus();
    }
  }

  /* ── interactions ─────────────────────────────────────────────────────── */
  rootEl.addEventListener('click', function (ev) {
    booted = true;
    var btn = ev.target.closest('button');
    if (!btn) return;

    if (btn.dataset.goto) {
      state.sheetOpen = false;
      render('#' + btn.dataset.goto + ' .hb-cat-title');
      var target = document.getElementById(btn.dataset.goto);
      if (target && target.scrollIntoView) target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      return;
    }
    if (btn.dataset.cat !== undefined) {
      var key = btn.dataset.cat, value = btn.dataset.value;
      if (state.picks[key] === value) return;
      var res = resolvePick(key, value);
      if (!res) return; // sold out everywhere (card is disabled)
      state.picks = res.picks;
      state.flash = res.changed.length ? ('Also updated to keep this build available — ' + res.changed.map(function (cc) { return cc.label + ': ' + splitValue(state.picks[cc.key]).primary; }).join('  ·  ')) : '';
      state.added = false; state.error = null;
      render('.hb-card[data-cat="' + key + '"][data-value="' + value.replace(/"/g, '\\"') + '"]');
      return;
    }
    if (btn.dataset.warranty !== undefined) {
      if (state.warranty === btn.dataset.warranty) return;
      state.warranty = btn.dataset.warranty;
      state.flash = '';
      state.added = false; state.error = null;
      render('.hb-card[data-warranty="' + btn.dataset.warranty.replace(/"/g, '\\"') + '"]');
      return;
    }
    if (btn.hasAttribute('data-share')) {
      writeHash();
      var done = function () {
        btn.textContent = 'LINK COPIED ✓';
        setTimeout(function () { btn.textContent = 'Copy Build Link'; }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(location.href).then(done, done);
      else done();
      return;
    }
    if (btn.hasAttribute('data-sheet-toggle')) {
      state.sheetOpen = !state.sheetOpen;
      render('[data-sheet-toggle]');
      return;
    }
    if (btn.hasAttribute('data-add')) addToCart();
  });

  function addToCart() {
    if (state.adding) return;
    if (state.added) { window.location.href = '/cart'; return; }
    var r = resolution();
    if (!r) return;
    state.adding = true; state.error = null;
    render();

    var items = [{ id: r.variant.id, quantity: 1 }];
    var w = null;
    for (var i = 0; i < WARRANTY.length; i++) if (WARRANTY[i].label === state.warranty) w = WARRANTY[i];
    if (w && w.variantId) items.push({ id: w.variantId, quantity: 1, properties: { _pc_variant_id: String(r.variant.id) } });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(items.length > 1 ? { items: items } : items[0])
    }).then(function (res) {
      if (!res.ok) return res.json().catch(function () { return {}; }).then(function (j) {
        throw new Error(j.description || 'Could not add to cart.');
      });
      return res.json().catch(function () { return {}; });
    }).then(function (resource) {
      state.adding = false; state.added = true;
      render();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: resource, sourceId: 'hachiman-builder', data: { source: 'hachiman-builder' } }
      }));
    }).catch(function (err) {
      state.adding = false;
      state.error = err.message || 'Network error — please try again.';
      render();
    });
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */
  var restored = readHash();
  if (restored.failed > 0) {
    bootNotice = 'Part of this shared build is no longer offered — those slots use defaults instead.';
  }
  render();

  window.HBUILDER = {
    state: state,
    cats: CATS,
    total: total,
    resolution: resolution,
    machines: MACHINES,
  };
})();
